import { Checkbox, FormControlLabel, TextField, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Dispatch } from "react";
import { getDisplayPhoneNumberFromFlattenedMembership } from "shared-ui/User/utils";
import { Role } from "../../../../types";
import RoleMultiSelect from "./RoleMultiSelect";
import { EditOrgMembershipFormAction, OrgMembershipFormState } from "./utils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    checkBoxDiv: {
      display: "flex",
      justifyContent: "space-around",
      marginTop: theme.spacing(2),
    },
    email: { marginRight: theme.spacing(2), width: `calc(50% - ${theme.spacing(1)})` },
    phoneNumber: { width: `calc(50% - ${theme.spacing(1)})` },
  })
);

interface EditOrgMembershipFormProps {
  state: OrgMembershipFormState;
  dispatch: Dispatch<EditOrgMembershipFormAction>;
  isTeamworksUser: boolean;
  isTeamworksUserManagementEnabled: boolean;
}

const EditOrgMembershipForm = ({ state, dispatch, isTeamworksUser, isTeamworksUserManagementEnabled }: EditOrgMembershipFormProps) => {
  const classes = useStyles();
  const athleteSelected = state.roles.includes("athlete");
  const originalHasDietitianRole = state.originalRoles.includes("dietitian");
  // Athlete can't be assigned to an existing dietitian (mutual exclusivity).
  const athleteOptionDisabled = !athleteSelected && originalHasDietitianRole;

  const rolesToShow: Role[] = ["dietitian", "chef", "foodmanager", "kiosk", "athlete", "nonRdAdmin"];

  // Athlete is mutually exclusive: while selected, every other option is disabled;
  // when the athlete option itself is unavailable, only athlete is disabled.
  const disabledRoles: Role[] = athleteSelected
    ? rolesToShow.filter(r => r !== "athlete")
    : athleteOptionDisabled
    ? ["athlete"]
    : [];

  const handleRolesChange = (next: Role[]) => {
    // Selecting athlete clears all other roles
    dispatch({ type: "CHANGE_ROLES", payload: next.includes("athlete") ? ["athlete"] : next });
  };

  return (
    <>
      <TextField
        className={classes.email}
        margin="dense"
        label="Email"
        onChange={e => dispatch({ type: "CHANGE_EMAIL", payload: e.target.value })}
        value={state.email}
        disabled={isTeamworksUser || isTeamworksUserManagementEnabled}
      />
      <TextField
        className={classes.phoneNumber}
        margin="dense"
        label="Phone #"
        onChange={e => dispatch({ type: "CHANGE_PHONE_NUMBER", payload: e.target.value })}
        value={getDisplayPhoneNumberFromFlattenedMembership(state, isTeamworksUserManagementEnabled) ?? ""}
        disabled={isTeamworksUser || isTeamworksUserManagementEnabled}
      />
      {isTeamworksUser && (
        <Typography variant="body1Medium">
          This user is linked to a Teamworks user. Please edit the phone number and/or email in Teamworks and re-run profile-sync in order
          to make these changes.
        </Typography>
      )}
      {isTeamworksUserManagementEnabled && (
        <Typography variant="body1Medium">
          This org is Axle synced. Email, phone number, and isActive cannot be edited. These fields are managed by Axle.
        </Typography>
      )}
      <RoleMultiSelect
        roles={rolesToShow}
        disabledRoles={disabledRoles}
        selectedRoles={state.roles}
        setSelectedRoles={handleRolesChange}
        required={true}
      />
      <div className={classes.checkBoxDiv}>
        <FormControlLabel
          value="admin"
          control={
            <Checkbox
              checked={state.isAdmin}
              onChange={e =>
                dispatch({
                  type: "CHANGE_IS_ADMIN",
                  payload: !state.isAdmin,
                })
              }
            />
          }
          label="Is Admin"
          labelPlacement="top"
        />
        <FormControlLabel
          value="active"
          control={
            <Checkbox
              checked={state.isActive}
              disabled={!state.accountActivated || isTeamworksUserManagementEnabled}
              onChange={e =>
                dispatch({
                  type: "CHANGE_IS_ACTIVE",
                  payload: !state.isActive,
                })
              }
            />
          }
          label="Is Active"
          labelPlacement="top"
        />
      </div>
    </>
  );
};

export default EditOrgMembershipForm;
